// src/modules/portal/pages/TenantPublicProfile.tsx
// Perfil público completo de uma empresa

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  MapPin, Phone, Mail, MessageCircle, Clock, 
  Star, ExternalLink, Share2, Heart, Instagram, Fish
} from 'lucide-react';
import { BreadcrumbNav } from '../components/BreadcrumbNav';
import { StarRating } from '../components/StarRating';
import { ReviewForm } from '../components/ReviewForm';
import { ReviewList } from '../components/ReviewList';
import { cloudFunctions } from '../../../core/services/firebase/cloudFunctions';
import { getTenantBySlug } from '../services/portalService';
import { analytics } from '../../../core/services/analytics';
import { SEO } from '../../../components/SEO';
import { Tenant } from '../../../core/types/tenant';
import { LegendBadge } from '../../../components/LegendBadge';
import { CommunityVouchButton } from '../components/CommunityVouchButton';
import { VerificationBadge } from '../../social/components/VerificationBadge';

export const TenantPublicProfile: React.FC = () => {
// ... (lines 23-165 omitted for brevity in thought, but tool needs exact match)
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [activeTab, setActiveTab] = useState<'sobre' | 'fotos' | 'avaliacoes'>('sobre');

  useEffect(() => {
    if (slug) {
      loadTenant(slug);
    }
  }, [slug]);

  const handleClaimProfile = async () => {
    if (!tenant) return;
    
    const email = window.prompt(`Para reivindicar o perfil de "${tenant.businessName}", informe seu email de contato comercial:`);
    
    if (email && email.includes('@')) {
      setClaiming(true);
      try {
        analytics.trackProfileClaim(tenant.id, tenant.businessName);
        const result = await cloudFunctions.validateProfileClaim(tenant.id, email);
        alert(result.message);
      } catch (err) {
        console.error('Claim error:', err);
        alert('Erro ao processar solicitação. Tente novamente mais tarde.');
      } finally {
        setClaiming(false);
      }
    } else if (email) {
      alert('Por favor, informe um email válido.');
    }
  };

  const loadTenant = async (slug: string) => {
// ... (rest of loadTenant)
    setLoading(true);
    const data = await getTenantBySlug(slug);
    setTenant(data);
    if (data) {
      analytics.trackProfileView(data.id, data.businessName);
    }
    setLoading(false);
  };

  const handleWhatsApp = () => {
    if (tenant?.portalProfile.contact.whatsapp) {
      const cleanNumber = tenant.portalProfile.contact.whatsapp.replace(/\D/g, '');
      const message = `Olá! Vi o perfil de ${tenant.businessName} no Nossa Pesca e gostaria de mais informações.`;
      window.open(`https://wa.me/${cleanNumber}?text=${encodeURIComponent(message)}`, '_blank');
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: tenant?.businessName,
          text: tenant?.portalProfile.shortDescription,
          url: window.location.href,
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      // Fallback: copiar URL
      navigator.clipboard.writeText(window.location.href);
      alert('Link copiado para a área de transferência!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-midnight flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-brand-gold mb-4"></div>
          <p className="text-white/60">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className="min-h-screen bg-brand-midnight flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4 text-brand-gold">🎣</div>
          <h1 className="text-3xl font-bold text-white mb-2 font-display">Empresa não encontrada</h1>
          <p className="text-white/60 mb-6 font-medium">A empresa que você está procurando não existe ou foi removida.</p>
          <button
            onClick={() => navigate('/')}
            className="bg-brand-orange text-white px-8 py-3 rounded-xl font-bold hover:shadow-glow-orange transition-all"
          >
            Voltar para o Início
          </button>
        </div>
      </div>
    );
  }

  const getTenantTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      'pousada': 'Pousada',
      'hotel': 'Hotel',
      'barco-hotel': 'Barco Hotel',
      'guia': 'Guia de Pesca',
      'agencia': 'Agência de Turismo',
      'loja': 'Loja',
      'influenciador': 'Influenciador',
      'associacao': 'Associação'
    };
    return labels[type] || type;
  };

  return (
    <div className="min-h-screen bg-brand-midnight flex flex-col">
      <SEO 
        title={tenant.businessName}
        description={tenant.portalProfile.shortDescription}
        ogImage={tenant.portalProfile.coverImage}
        ogType="profile"
      />

      {/* Cover Image */}
      <div className="relative h-64 md:h-96 bg-gradient-to-br from-blue-600 to-blue-900">
        {tenant.portalProfile.coverImage ? (
          <img 
            src={tenant.portalProfile.coverImage} 
            alt={tenant.businessName}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <span className="text-9xl">🎣</span>
          </div>
        )}
        
        {/* Overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
        
        {/* Badges - Adjusted for mobile stacking */}
        <div className="absolute top-24 right-4 md:right-6 flex flex-col md:flex-row gap-2 md:top-32">
          {tenant.portalProfile.isFishingLegend && tenant.portalProfile.legendTier && (
            <LegendBadge 
              level={((tenant.portalProfile.legendTier as any) === 'influenciador' ? 'influencer' : tenant.portalProfile.legendTier) as 'legend' | 'pro' | 'influencer'} 
              showText={true}
              className="shadow-glowScale"
            />
          )}
          <VerificationBadge 
            trustLevel={tenant.portalProfile.trustLevel} 
            level={tenant.portalProfile.verifiedBadge ? 'silver' : 'none'} 
            showText={true}
            className="bg-brand-blue text-brand-dark px-3 py-1.5 md:px-4 md:py-2 rounded-full shadow-lg"
          />
          {tenant.portalProfile.isFeatured && (
            <span className="bg-brand-yellow text-brand-dark px-3 py-1.5 md:px-4 md:py-2 rounded-full font-semibold text-xs md:text-sm shadow-lg whitespace-nowrap">
              ⭐ Destaque
            </span>
          )}
        </div>
        
        {/* Share & Favorite - Adjusted position for header */}
        <div className="absolute top-24 left-4 md:left-6 flex gap-2 md:top-32">
          <button
            onClick={handleShare}
            className="bg-white/90 backdrop-blur-sm p-3 rounded-full hover:bg-white transition-colors text-brand-dark"
            title="Compartilhar"
          >
            <Share2 size={20} />
          </button>
          <button
            className="bg-white/90 backdrop-blur-sm p-3 rounded-full hover:bg-white transition-colors text-brand-dark"
            title="Adicionar aos favoritos"
          >
            <Heart size={20} />
          </button>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="container mx-auto px-6 -mt-20 relative z-10 pb-12 flex-grow">
        {tenant.isShadow && (
          <div className="bg-gradient-to-r from-brand-orange to-brand-orange-light text-white p-6 rounded-xl mb-8 shadow-glow flex flex-col md:flex-row items-center justify-between gap-6 animate-pulse-subtle">
            <div className="flex items-center gap-4">
              <div className="bg-white/20 p-3 rounded-full">
                <Fish size={28} />
              </div>
              <div>
                <h4 className="font-bold text-xl">Este perfil ainda não foi reivindicado</h4>
                <p className="text-white/90">Você é o proprietário da {tenant.businessName}? Reivindique agora sua conta e comece a vender no portal!</p>
              </div>
            </div>
            <button 
              onClick={handleClaimProfile} 
              disabled={claiming}
              className={`bg-white text-brand-orange hover:bg-gray-100 px-8 py-3 rounded-full font-bold transition-all shadow-lg whitespace-nowrap text-lg ${claiming ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {claiming ? 'PROCESSANDO...' : 'REIVINDICAR PERFIL'}
            </button>
            
            <div className="hidden md:block w-px h-12 bg-white/20 mx-2"></div>

            <div className="flex flex-col items-center">
              <span className="text-[10px] font-bold uppercase tracking-widest opacity-80 mb-2">Conhece este local?</span>
              <CommunityVouchButton 
                tenantId={tenant.id}
                businessName={tenant.businessName}
                portalProfile={tenant.portalProfile}
              />
            </div>
          </div>
        )}

        {/* Breadcrumb */}
        <div className="mb-6">
          <BreadcrumbNav 
            items={[
              { label: 'Estados', path: '/estados' },
              { label: tenant.location.stateName || tenant.location.state, path: `/estados/${tenant.location.state.toLowerCase()}` },
              { label: tenant.businessName }
            ]}
          />
        </div>
        
        {/* Header Card */}
        <div className="bg-brand-charcoal/50 backdrop-blur-xl border border-white/10 rounded-3xl p-8 mb-8">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
            <div className="flex-grow">
              <div className="flex flex-col md:flex-row md:items-center gap-3 mb-3">
                <h1 className="text-4xl font-black text-white tracking-tight">{tenant.businessName}</h1>
                <div className="flex items-center gap-2">
                  <StarRating rating={tenant.averageRating || 0} size={20} />
                  <span className="text-sm font-bold text-white/40">
                    {tenant.averageRating?.toFixed(1) || '0.0'} ({tenant.reviewCount || 0} avaliações)
                  </span>
                  {tenant.portalProfile.mentionCount && tenant.portalProfile.mentionCount > 0 && (
                    <span className="text-sm font-bold text-amber-400/80 flex items-center gap-1">
                      • 📸 {tenant.portalProfile.mentionCount} menções
                    </span>
                  )}
                </div>
              </div>
              
              <div className="flex flex-wrap items-center gap-4 text-white/40 mb-4">
                <span className="flex items-center gap-2 font-bold">
                  <MapPin size={18} className="text-brand-gold" />
                  {tenant.location.city}, {tenant.location.state}
                </span>
                <span className="px-3 py-1 bg-white/5 text-brand-gold rounded-full text-xs font-black uppercase tracking-widest border border-white/10">
                  {getTenantTypeLabel(tenant.tenantType)}
                </span>
              </div>
              
              <p className="text-lg text-white/60 mb-6 font-medium leading-relaxed">
                {tenant.portalProfile.shortDescription}
              </p>
              
              {/* Highlights */}
              {tenant.portalProfile.highlights && tenant.portalProfile.highlights.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {tenant.portalProfile.highlights.map((highlight: string, idx: number) => (
                    <span key={idx} className="bg-white/5 text-brand-gold border border-brand-gold/20 px-3 py-1 rounded-full text-xs font-bold">
                      ✓ {highlight}
                    </span>
                  ))}
                </div>
              )}
            </div>
            
            {/* Contact Card */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 min-w-[300px]">
              <h3 className="font-black text-white/30 text-xs uppercase tracking-widest mb-4">Entre em Contato</h3>
              
              <div className="space-y-3">
                {tenant.portalProfile.contact.whatsapp && (
                  <button
                    onClick={handleWhatsApp}
                    className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-3 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors"
                  >
                    <MessageCircle size={20} />
                    WhatsApp
                  </button>
                )}
                
                {tenant.portalProfile.contact.phone && (
                  <a
                    href={`tel:${tenant.portalProfile.contact.phone}`}
                    className="w-full bg-brand-blue hover:bg-blue-400 text-brand-dark font-semibold py-3 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors"
                  >
                    <Phone size={20} />
                    {tenant.portalProfile.contact.phone}
                  </a>
                )}
                
                {tenant.portalProfile.contact.email && (
                  <a
                    href={`mailto:${tenant.portalProfile.contact.email}`}
                    className="w-full border-2 border-gray-300 hover:border-brand-blue text-gray-700 font-semibold py-3 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors"
                  >
                    <Mail size={18} />
                    Email
                  </a>
                )}
                
                {tenant.domain && (
                  <a
                    href={`https://${tenant.domain}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full border-2 border-gray-300 hover:border-brand-blue text-gray-700 font-semibold py-3 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors"
                  >
                    <ExternalLink size={18} />
                    Visitar Site
                  </a>
                )}
              </div>
              
              {/* Social Media */}
              {tenant.portalProfile.contact.instagram && (
                <div className="mt-4 pt-4 border-t">
                  <a
                    href={`https://instagram.com/${tenant.portalProfile.contact.instagram.replace('@', '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-pink-600 hover:text-pink-700"
                  >
                    <Instagram size={20} />
                    @{tenant.portalProfile.contact.instagram.replace('@', '')}
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="border-b">
            <div className="flex">
              <button
                onClick={() => setActiveTab('sobre')}
                className={`flex-1 py-4 px-6 font-semibold transition-colors ${
                  activeTab === 'sobre' 
                    ? 'text-brand-orange border-b-2 border-brand-orange' 
                    : 'text-gray-600 hover:text-brand-orange'
                }`}
              >
                Sobre
              </button>
              <button
                onClick={() => setActiveTab('fotos')}
                className={`flex-1 py-4 px-6 font-semibold transition-colors ${
                  activeTab === 'fotos' 
                    ? 'text-brand-orange border-b-2 border-brand-orange' 
                    : 'text-gray-600 hover:text-brand-orange'
                }`}
              >
                Fotos
              </button>
              <button
                onClick={() => setActiveTab('avaliacoes')}
                className={`flex-1 py-4 px-6 font-semibold transition-colors ${
                  activeTab === 'avaliacoes' 
                    ? 'text-brand-orange border-b-2 border-brand-orange' 
                    : 'text-gray-600 hover:text-brand-orange'
                }`}
              >
                Avaliações
              </button>
            </div>
          </div>
          
          <div className="p-8">
            {activeTab === 'sobre' && (
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Sobre {tenant.businessName}</h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  {tenant.description || tenant.portalProfile.shortDescription}
                </p>
                
                {/* Tags */}
                {tenant.categoryTags && tenant.categoryTags.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-3">Categorias</h3>
                    <div className="flex flex-wrap gap-2">
                      {tenant.categoryTags.map((tag: string, idx: number) => (
                        <span key={idx} className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg text-sm">
                          #{tag.replace(/-/g, ' ')}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {activeTab === 'fotos' && (
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-6">Galeria de Fotos</h2>
                {tenant.portalProfile.gallery && tenant.portalProfile.gallery.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {tenant.portalProfile.gallery.map((photo: string, idx: number) => (
                      <img 
                        key={idx}
                        src={photo} 
                        alt={`Foto ${idx + 1}`}
                        className="w-full h-64 object-cover rounded-lg hover:opacity-90 transition-opacity cursor-pointer"
                      />
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-12">Nenhuma foto disponível</p>
                )}
              </div>
            )}
            
            {activeTab === 'avaliacoes' && (
              <div className="max-w-4xl mx-auto">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-12 bg-blue-50 p-8 rounded-2xl">
                  <div>
                    <h2 className="text-3xl font-black text-gray-900 mb-2">O que os pescadores dizem</h2>
                    <p className="text-gray-600">Baseado em {tenant.reviewCount || 0} avaliações de clientes reais.</p>
                  </div>
                  <div className="flex flex-col items-center bg-white p-6 rounded-xl shadow-sm border border-blue-100 min-w-[150px]">
                    <span className="text-5xl font-black text-gray-900 mb-1">{tenant.averageRating?.toFixed(1) || '0.0'}</span>
                    <StarRating rating={tenant.averageRating || 0} size={20} />
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                  <div className="lg:col-span-2 order-2 lg:order-1">
                    <ReviewList tenantId={tenant.id} />
                  </div>
                  <div className="order-1 lg:order-2">
                    <div className="sticky top-24">
                      <ReviewForm 
                        tenantId={tenant.id} 
                        onSuccess={() => loadTenant(slug!)} 
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
    </div>
  );
};
